import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DuplicateStatus, WorkspaceDocument } from 'scribe-web-shared/types/document';
import scribeDocumentService from 'services/scribeDocumentService';
import scribeFileService from 'services/scribeFileService';
import { scribeShareURLv2 } from 'utils/humanReadableURL';
import { trackEvent } from 'utils/tracking';

interface ScribeDuplicate {
  id: string;
  url: string;
  status: DuplicateStatus;
}

export type Section = 'document_actions' | 'step_actions' | 'customization' | 'collaboration' | 'review' | 'stats_and_info';

const initialSection: Section = 'document_actions';

interface EditorSidebarState {
  openSection?: Section;
  sourceToDuplicate: { [id: string]: ScribeDuplicate };
}

const initialState: EditorSidebarState = {
  sourceToDuplicate: {}
};

export const duplicateScribe = createAsyncThunk<ScribeDuplicate, string>('editorSidebar/duplicate', async (id) => {
  const newScribe = (await scribeDocumentService.duplicateScribeDocument(id)) as WorkspaceDocument;

  trackEvent('duplicate_scribe', { sourceScribeId: id, location: 'editor_document_actions', version: 'legacy' });
  return {
    id: newScribe.id,
    url: `${scribeShareURLv2(newScribe.id, newScribe.name)}?back_to=browser`,
    status: newScribe.duplicate_status
  };
});

// TODO Would be nice to have a dedicated endpoint that returns only this field.
export const getDuplicateStatus = createAsyncThunk<DuplicateStatus, string>('editorSidebar/getDuplicateStatus', async (id) => {
  const scribeFile = await scribeFileService.getScribeFile({ id });

  return scribeFile.duplicate_status;
});

const editorSidebarSlice = createSlice({
  name: 'editorSidebar',
  initialState,
  reducers: {
    init: (state) => {
      state.openSection = initialSection;
    },
    toggle: (state, action: PayloadAction<Section>) => {
      const section = action.payload;

      if (state.openSection === section) {
        delete state.openSection;
      } else {
        state.openSection = section;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(duplicateScribe.fulfilled, (state, action) => {
      state.sourceToDuplicate[action.meta.arg] = action.payload;
    });
    builder.addCase(getDuplicateStatus.fulfilled, (state, action) => {
      const id = action.meta.arg;
      const sourceId = Object.keys(state.sourceToDuplicate).find((k) => state.sourceToDuplicate[k].id === id);

      if (sourceId) {
        state.sourceToDuplicate[sourceId].status = action.payload;
      }
    });
  }
});

export const { init, toggle } = editorSidebarSlice.actions;

export default editorSidebarSlice.reducer;
